import {
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import classnames from 'classnames'
import IframeResizer from 'iframe-resizer-react'
import React, { useRef, useState } from 'react'
import AspectRatio from 'react-aspect-ratio'

import LoadingSpinner from '../__legacy/UI/LoadingSpinner'

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(6),
  },
  chartArea: {
    position: 'relative',
    display: 'block',
    '&.loading': {
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: theme.palette.grey[200],
      borderRadius: theme.spacing(1),
    },
  },
  aspectRatio: {
    position: 'relative',
    '& > img': {
      height: 'auto',
    },
    '& > :first-child': {
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '100%',
    },

    '&::before': {
      content: '""',
      display: 'block',
      width: '100%',
    },

    '@supports not (aspect-ratio: 1/1)': {
      '&::before': {
        height: 0,
        paddingBottom: 'calc(100% / (var(--aspect-ratio)))',
      },
    },

    '@supports (aspect-ratio: 1/1)': {
      '&::before': {
        aspectRatio: 'calc(var(--aspect-ratio))',
      },
    },
  },
  frame: {
    '&.loading': {
      visibility: 'hidden',
    },
  },
  helpText: {
    marginTop: theme.spacing(1),
    fontStyle: 'italic',
  },
  loading: {
    width: '100%',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    top: theme.spacing(4),
    position: 'absolute',
  },
  loadingText: {
    fontStyle: 'italic',
    color: theme.palette.grey[500],
  },
}))

type MetabaseEmbedProps = {
  /** Link to the Metabase chart or dashboard embed */
  src: string
  /** Is the embed a whole dashboard, or a single chart */
  embedType: 'dashboard' | 'chart'
  /**
   * If set, will hide the title provided by Metabase, and instead use the text
   * provided, rendered within a `<Typography />` component
   */
  title?: React.ReactNode
  /** Adds helper text after the embedded dashboard */
  helpText?: React.ReactNode
}

/**
 * Component to embed charts or dashboards from Metabase
 *
 * If the embed is a Dashboard, it uses the IFrameResizer component to get the
 * frame height, and resize it accordingly. Dashboards (and their associated
 * chart components) have a fixed aspect ratio.
 *
 * If the embed is a Chart, it uses the AspectRatio component to set an
 * appropriate aspect ratio to display the chart at for the current viewport.
 * Charts resize to fill their containers.
 */
const MetabaseEmbed: React.FC<MetabaseEmbedProps> = ({
  src,
  embedType,
  title,
  helpText,
}) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.only('xs'))
  const isSm = useMediaQuery(theme.breakpoints.only('sm'))
  const aspect = isXs ? 0.75 : isSm ? 1 : 2
  // Metabase will hide a chart title if the URL fragment contains `titled=false`
  const _src = title ? `${src}#titled=false` : src

  return (
    <div className={classes.root}>
      {title && (
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>
      )}
      <div className={classnames(classes.chartArea, { loading })}>
        {loading && (
          <div className={classes.loading}>
            <LoadingSpinner />
            <Typography variant="body2" className={classes.loadingText}>
              Initializing, this might take a few seconds...
            </Typography>
          </div>
        )}
        {embedType === 'dashboard' ? (
          <IframeResizer
            forwardRef={iframeRef}
            src={_src}
            frameBorder="0"
            width="100%"
            height="800"
            onLoad={() => setLoading(false)}
            className={classnames(classes.frame, { loading })}
          />
        ) : (
          <AspectRatio ratio={aspect} className={classes.aspectRatio}>
            <iframe
              src={_src}
              frameBorder="0"
              width="100%"
              height="100%"
              onLoad={() => setLoading(false)}
              className={classnames(classes.frame, {
                loading,
              })}
            />
          </AspectRatio>
        )}
      </div>
      {helpText && (
        <Typography className={classes.helpText} variant="body2">
          {helpText}
        </Typography>
      )}
    </div>
  )
}

export default MetabaseEmbed
